import type { ComponentProps, ComponentType, PropsWithChildren } from "react";
import { Fragment } from "react";
import type { VariantProps } from "tailwind-variants";
import { tv } from "tailwind-variants";

import type { SvgSlot } from "@/shared/ui";

const TAGS = {
  1: "h1",
  2: "h2",
  3: "h3",
} as const;

const variants = tv({
  base: "items-baseline gap-1 font-semibold has-[svg]:inline-flex [&>svg]:size-[1em] [&>svg]:translate-y-[0.125em]",
  variants: {
    size: {
      "xx-large": "text-3xl sm:text-5xl",
      "x-large": "text-xl sm:text-4xl",
      large: "text-xl sm:text-3xl",
      medium: "text-xl sm:text-2xl",
      small: "text-base sm:text-xl",
      "x-small": "text-base sm:text-lg",
      "xx-small": "text-base sm:text-base",
    },
    weight: {
      weak: "font-normal",
      medium: "font-medium",
    },
    color: {
      neutral: "text-neutral-950",
      primary: "text-primary-950",
      secondary: "text-secondary-500",

      warning: "text-yellow-950",
    },
    align: {
      center: "text-center",
    },
  },
  defaultVariants: {
    color: "neutral",
  },
});

const styles = tv({
  variants: {
    level: {
      1: variants({ size: "large" }),
      2: variants({ size: "medium" }),
      3: variants({ size: "x-small" }),
    },
  },
});

type HeadingProps = PropsWithChildren<
  VariantProps<typeof styles> &
    VariantProps<typeof variants> & {
      icon?: ComponentType<ComponentProps<typeof SvgSlot>>;
    }
>;

export const Heading = ({
  level = 1,
  size,
  weight,
  color,
  children,
  icon: Icon = Fragment,
  ...props
}: HeadingProps) => {
  const Component = TAGS[level];

  const className = variants({ size, weight, color });

  return (
    <Component
      data-slot="heading"
      className={styles({ level, className })}
      {...props}
    >
      <Icon /> {children}
    </Component>
  );
};
